import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  Building2, Users, Wallet, Clock, Copy, LogOut, Plus, Upload, Check, PauseCircle, PlayCircle, ExternalLink,
  TrendingUp, CheckCircle2, FileText, ImagePlus, KeyRound, RefreshCw, X, Mail, Download, HandCoins,
  LayoutDashboard,
} from "lucide-react";
import { alpha, readableOn, themeOf, heroSrcOf, PLAIN_HERO } from "@/lib/portalTheme";
import EarningsCharts from "@/components/partner/EarningsCharts";

const usd2 = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const usd = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const inputStyle = { border: "1.5px solid rgba(15,18,38,0.12)" };
const inputCls = "w-full mt-1 px-3 py-2.5 rounded-xl text-sm";

const resizeLogo = (file, px = 256) => new Promise((resolve, reject) => {
  const img = new Image(); const fr = new FileReader();
  img.onload = () => {
    const c = document.createElement("canvas");
    const scale = Math.min(px / img.width, px / img.height, 1);
    c.width = Math.round(img.width * scale); c.height = Math.round(img.height * scale);
    c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
    resolve(c.toDataURL("image/png"));
  };
  img.onerror = () => reject(new Error("decode"));
  fr.onload = () => { img.src = fr.result; };
  fr.onerror = () => reject(new Error("read"));
  fr.readAsDataURL(file);
});

// Photos go in as JPEG, resized in the browser so a 6MB camera file doesn't
// end up on the portal. Quality steps down until it fits the server's cap.
const resizePhoto = (file, maxW = 1600) => new Promise((resolve, reject) => {
  const img = new Image(); const fr = new FileReader();
  img.onload = () => {
    const c = document.createElement("canvas");
    const scale = Math.min(maxW / img.width, 1);
    c.width = Math.round(img.width * scale); c.height = Math.round(img.height * scale);
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#ffffff"; ctx.fillRect(0, 0, c.width, c.height);
    ctx.drawImage(img, 0, 0, c.width, c.height);
    let out = c.toDataURL("image/jpeg", 0.82);
    for (const q of [0.72, 0.62, 0.5]) {
      if (out.length <= 1_300_000) break;
      out = c.toDataURL("image/jpeg", q);
    }
    resolve(out);
  };
  img.onerror = () => reject(new Error("decode"));
  fr.onload = () => { img.src = fr.result; };
  fr.onerror = () => reject(new Error("read"));
  fr.readAsDataURL(file);
});

const HERO_PRESETS = [
  ["private-office", "Private office"],
  ["glass-tower", "Glass tower"],
  ["city-dusk", "City at dusk"],
  ["city-skyline", "Skyline"],
  ["dark-marble", "Dark marble"],
  ["still-water", "Still water"],
];

const TABS = ["Overview", "Clients", "Earnings", "Offers", "Branding"];

function Overview({ me, portalUrl }) {
  const s = me.stats;
  const t = me.partner.terms || {};
  return (
    <div className="space-y-4" data-testid="partner-overview">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[["Clients", s.clients, Users], ["Awaiting your review", s.applied, Clock],
          ["Funded capital", usd(s.funded_usdt), Wallet], ["Awaiting funds", usd(s.awaiting_usdt), Clock]].map(([k, v, Icon]) => (
          <div key={k} className="glass p-4">
            <div className="flex items-center gap-2 kpi-label"><Icon size={12} /> {k}</div>
            <div className="font-display text-2xl font-semibold mt-1">{v}</div>
          </div>
        ))}
      </div>
      <div className="glass p-4">
        <div className="kpi-label">Your client portal</div>
        <div className="flex items-center gap-2 mt-1.5 flex-wrap">
          <span className="font-mono text-[13px]" data-testid="partner-portal-url">{portalUrl}</span>
          <button onClick={() => { navigator.clipboard?.writeText(portalUrl); toast.success("Link copied"); }}
            className="btn-secondary" data-testid="copy-portal-link"><Copy size={13} /> Copy</button>
          <a href={portalUrl} target="_blank" rel="noreferrer" className="btn-secondary" data-testid="open-portal-link"><ExternalLink size={13} /> Open</a>
        </div>
      </div>
      <div className="glass p-4 text-sm">
        <div className="kpi-label mb-1.5">Your agreement</div>
        <div className="text-ink-2">
          You keep <b className="text-ink">{t.margin_split_partner_pct}%</b> of everything earned above what you promise your clients.
          You may promise <b className="text-ink">{t.promise_min_pct}%–{t.promise_max_pct}%</b> a month, on tickets of{" "}
          <b className="text-ink">{usd(t.min_ticket)}–{usd(t.max_ticket)}</b>.
        </div>
      </div>
    </div>
  );
}

function PayoutRequests({ onChange }) {
  const [rows, setRows] = useState(null);
  const [busyId, setBusyId] = useState("");

  const load = async () => setRows((await api.get("/partner/payout-requests")).data.requests);
  useEffect(() => { load(); }, []);

  const act = async (rid, what) => {
    const note = window.prompt(what === "settle"
      ? "Anything to tell your client? (optional — shown on their statement)"
      : "Why not right now? (optional — shown on their statement)");
    if (note === null) return;
    setBusyId(rid);
    try {
      await api.post(`/partner/payout-requests/${rid}/${what}`, { note });
      toast.success(what === "settle" ? "Marked as paid — their statement now agrees" : "Request closed");
      load(); onChange?.();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    setBusyId("");
  };

  const open = (rows || []).filter((x) => x.status === "open");
  if (!rows || (!open.length && !rows.length)) return null;

  return (
    <div className="glass p-5" data-testid="payout-requests">
      <div className="flex items-center gap-2.5 mb-3">
        <HandCoins size={15} style={{ color: "#B45309" }} />
        <h3 className="font-display text-lg font-semibold tracking-tight">Payout requests</h3>
        {!!open.length && <span className="chip chip-amber" data-testid="payout-open-count">{open.length} waiting on you</span>}
      </div>
      {!open.length && <div className="text-sm text-ink-2" data-testid="payout-none-open">Nothing outstanding — your clients have all been dealt with.</div>}
      {open.map((x) => (
        <div key={x.id} className="flex items-center gap-3 flex-wrap py-2.5 border-t" style={{ borderColor: "rgba(15,18,38,0.06)" }} data-testid={`payout-row-${x.client_reference}`}>
          <div className="min-w-0 flex-1">
            <div className="text-sm"><b>{x.client_name}</b> ({x.client_reference}) asked for <b>{usd(x.amount)}</b></div>
            <div className="text-[11.5px] text-ink-3">
              {String(x.created_at).slice(0, 10)}{x.note ? ` · “${x.note}”` : ""}
            </div>
          </div>
          <button onClick={() => act(x.id, "settle")} disabled={busyId === x.id} className="btn-primary !py-2 !px-3 text-sm" data-testid={`payout-settle-${x.client_reference}`}>
            <Check size={13} /> I've paid this
          </button>
          <button onClick={() => act(x.id, "decline")} disabled={busyId === x.id} className="btn-secondary" data-testid={`payout-decline-${x.client_reference}`}>
            Not now
          </button>
        </div>
      ))}
      {rows.filter((x) => x.status !== "open").slice(0, 3).map((x) => (
        <div key={x.id} className="flex items-center gap-2 py-1.5 border-t text-[12.5px] text-ink-3" style={{ borderColor: "rgba(15,18,38,0.06)" }}>
          <span className={`chip ${x.status === "settled" ? "chip-emerald" : ""}`}>{x.status}</span>
          {x.client_name} · {usd(x.amount)} · {String(x.settled_at || x.created_at).slice(0, 10)}
        </div>
      ))}
    </div>
  );
}

function Clients({ offers, reload }) {
  const [rows, setRows] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", country: "", offer_id: "", amount: "" });
  const [adding, setAdding] = useState(false);
  const [busyId, setBusyId] = useState("");
  const [cred, setCred] = useState(null); // { email, password, created }

  const load = async () => setRows((await api.get("/partner/clients")).data.clients);
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    try { await api.post(`/partner/clients/${id}/status`, { status }); load(); reload(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const issueLogin = async (c, emailIt) => {
    setBusyId(c.id);
    try {
      const { data } = await api.post(`/partner/clients/${c.id}/login`, emailIt ? { email_it: true } : {});
      setCred({ ...data, emailIt });
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    setBusyId("");
  };

  const toggleLogin = async (c) => {
    const active = !c.login_active;
    if (!active && !window.confirm(`Switch off ${c.name}'s login? Their file, money and statement link stay as they are — they just can't sign in.`)) return;
    setBusyId(c.id);
    try {
      await api.post(`/partner/clients/${c.id}/login-status`, { active });
      toast.success(active ? "Login switched back on" : "Login switched off");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    setBusyId("");
  };

  const reissue = async (c) => {
    if (!window.confirm(`Re-issue ${c.name}'s statement link? The old link stops working straight away.`)) return;
    setBusyId(c.id);
    try {
      const { data } = await api.post(`/partner/clients/${c.id}/reissue-link`);
      navigator.clipboard?.writeText(data.statement_url);
      toast.success("New link issued and copied");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    setBusyId("");
  };

  const add = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/partner/clients", { ...form, amount: Number(form.amount) });
      toast.success(`Added · ${data.reference}`);
      setForm({ name: "", email: "", phone: "", country: "", offer_id: "", amount: "" });
      setAdding(false); load(); reload();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-3" data-testid="partner-clients">
      <PayoutRequests onChange={() => { load(); reload(); }} />
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-3">Your clients</span>
        <button onClick={() => setAdding((v) => !v)} className="btn-secondary" data-testid="partner-add-client-toggle"><Plus size={13} /> Add a client</button>
      </div>

      {adding && (
        <form onSubmit={add} className="glass p-4 grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="partner-add-client-form">
          <label className="block"><span className="kpi-label">Name</span>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} style={inputStyle} data-testid="client-name" /></label>
          <label className="block"><span className="kpi-label">Email</span>
            <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} style={inputStyle} data-testid="client-email" /></label>
          <label className="block"><span className="kpi-label">Phone</span>
            <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} style={inputStyle} data-testid="client-phone" /></label>
          <label className="block"><span className="kpi-label">Country</span>
            <input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className={inputCls} style={inputStyle} data-testid="client-country" /></label>
          <label className="block"><span className="kpi-label">Offer</span>
            <select value={form.offer_id} onChange={(e) => setForm({ ...form, offer_id: e.target.value })} className={inputCls} style={inputStyle} data-testid="client-offer">
              <option value="">Pick an offer…</option>
              {offers.filter((o) => o.status === "active").map((o) => (
                <option key={o.id} value={o.id}>{o.name} · {o.promised_rate_pct}%/mo · {o.term_months}m</option>
              ))}
            </select></label>
          <label className="block"><span className="kpi-label">Amount (USDT)</span>
            <input value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className={inputCls} style={inputStyle} data-testid="client-amount" /></label>
          <div className="col-span-2 md:col-span-3">
            <button type="submit" className="btn-primary" data-testid="client-save">Save client</button>
          </div>
        </form>
      )}

      {!rows ? <div className="glass p-6 text-sm text-ink-2">Loading…</div>
        : !rows.length ? <div className="glass p-8 text-center text-sm text-ink-2" data-testid="partner-clients-empty">No clients yet — share your portal link, or add one here.</div>
          : (
            <div className="glass p-0 overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-[11px] uppercase tracking-wider text-ink-3">
                  <th className="px-4 py-2.5">Client</th><th>Country</th><th>Commitment</th><th>Promise</th><th>Status</th><th>Sign-in</th><th></th>
                </tr></thead>
                <tbody>
                  {rows.map((c) => {
                    const d = c.deposits?.[0];
                    return (
                      <tr key={c.id} className="border-t" style={{ borderColor: "rgba(15,18,38,0.06)" }} data-testid={`client-row-${c.email}`}>
                        <td className="px-4 py-2.5">
                          <div className="font-semibold">{c.name}</div>
                          <div className="text-[11.5px] text-ink-3">{c.email} · {c.reference}</div>
                        </td>
                        <td className="text-ink-2">{c.country || "—"}</td>
                        <td className="font-semibold">{d ? usd(d.amount) : "—"}</td>
                        <td className="text-ink-2">{d ? `${d.promised_rate_pct}% · ${d.term_months}m · ${d.payout_mode}` : "—"}</td>
                        <td><span className={`chip ${c.status === "funded" ? "chip-emerald" : c.status === "declined" ? "chip-rose" : ""}`}>{c.status}</span></td>
                        <td>
                          {c.has_login
                            ? (c.login_active
                              ? <span className="chip chip-emerald" data-testid={`client-login-state-${c.email}`}>can sign in</span>
                              : <span className="chip chip-amber" data-testid={`client-login-state-${c.email}`}>switched off</span>)
                            : <span className="chip" data-testid={`client-login-state-${c.email}`}>no login</span>}
                        </td>
                        <td className="text-right px-4 whitespace-nowrap">
                          {c.statement_url && (
                            <>
                              <button onClick={() => { navigator.clipboard?.writeText(c.statement_url); toast.success("Statement link copied — safe to send to your client"); }}
                                className="btn-secondary mr-1.5" data-testid={`statement-link-${c.email}`}>
                                <FileText size={13} /> Statement
                              </button>
                              <button onClick={() => window.open(c.statement_pdf_url, "_blank")}
                                title="Download their statement as a one-page PDF"
                                className="btn-secondary mr-1.5" data-testid={`statement-pdf-${c.email}`}>
                                <Download size={13} /> PDF
                              </button>
                            </>
                          )}
                          <button onClick={() => reissue(c)} disabled={busyId === c.id} title="Issue a fresh statement link — the old one stops working"
                            className="btn-secondary mr-1.5" data-testid={`reissue-link-${c.email}`}>
                            <RefreshCw size={13} />
                          </button>
                          <button onClick={() => issueLogin(c, false)} disabled={busyId === c.id}
                            title={c.has_login ? "Set them a new password and show it to me" : "Create a login and show me the password"}
                            className="btn-secondary mr-1.5" data-testid={`client-password-${c.email}`}>
                            <KeyRound size={13} /> {c.has_login ? "New password" : "Create login"}
                          </button>
                          <button onClick={() => issueLogin(c, true)} disabled={busyId === c.id}
                            title="Create it and email the details to your client in your firm's name"
                            className="btn-secondary mr-1.5" data-testid={`client-email-login-${c.email}`}>
                            <Mail size={13} /> Email it
                          </button>
                          {c.has_login && (
                            <button onClick={() => toggleLogin(c)} disabled={busyId === c.id}
                              title={c.login_active ? "Switch their login off for now" : "Switch their login back on"}
                              className="btn-secondary mr-1.5" data-testid={`toggle-client-login-${c.email}`}>
                              {c.login_active ? <PauseCircle size={13} /> : <PlayCircle size={13} />}
                            </button>
                          )}
                          {c.status === "applied" && (
                            <span className="inline-flex gap-1.5">
                              <button onClick={() => setStatus(c.id, "approved")} className="btn-secondary" data-testid={`approve-${c.email}`}><Check size={13} /> Approve</button>
                              <button onClick={() => setStatus(c.id, "declined")} className="btn-secondary" data-testid={`decline-${c.email}`}>Decline</button>
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

      {cred && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4"
          style={{ background: "rgba(15,18,38,0.55)", backdropFilter: "blur(8px)" }}
          onClick={() => setCred(null)} data-testid="client-cred-modal">
          <div className="glass p-6 w-full max-w-md relative" onClick={(e) => e.stopPropagation()}
            style={{ background: "rgba(255,255,255,0.98)" }}>
            <button type="button" onClick={() => setCred(null)} className="absolute top-3 right-3 org-icon-btn" data-testid="client-cred-close">
              <X size={15} />
            </button>
            <div className="font-display text-xl font-semibold tracking-tight">
              {cred.created ? "Your client can now sign in" : "New password set"}
            </div>
            <p className="text-[12.5px] text-ink-2 mt-1">
              {cred.emailed
                ? "Emailed to them just now in your firm's name and colour — nothing of ours appears on it."
                : cred.email_attempted
                  ? "We tried to email it in your firm's name but it wouldn't send — pass it on yourself for now."
                  : "Shown once. Pass it to your client yourself — we don't email it, so nothing reaches them that you haven't written."}
            </p>
            <div className="glass p-3 mt-3" style={{ background: "rgba(0,134,58,0.06)" }}>
              <div className="text-[11px] text-ink-3">{cred.email}</div>
              <div className="font-mono text-[15px] font-semibold mt-0.5" data-testid="client-cred-password">{cred.password}</div>
              <div className="text-[11px] text-ink-3 mt-1.5">They sign in on your own site: <span className="font-mono">{cred.login_url}</span></div>
            </div>
            <div className="flex items-center gap-2 mt-4">
              <button type="button" className="btn-secondary !py-2 !px-3 text-sm flex-1" data-testid="client-cred-copy"
                onClick={() => { navigator.clipboard?.writeText(`${cred.email}  ${cred.password}  ${cred.login_url}`); toast.success("Copied"); }}>
                <Copy size={13} /> Copy
              </button>
              <button type="button" onClick={() => setCred(null)} className="btn-primary !py-2 !px-3 text-sm flex-1 justify-center" data-testid="client-cred-done">Done</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Offers({ offers, terms, reload }) {
  const [form, setForm] = useState({ name: "", promised_rate_pct: "", term_months: "12", payout_mode: "monthly", min_ticket: "", max_ticket: "" });

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/partner/offers", {
        name: form.name, promised_rate_pct: Number(form.promised_rate_pct),
        term_months: Number(form.term_months), payout_mode: form.payout_mode,
        min_ticket: form.min_ticket ? Number(form.min_ticket) : undefined,
        max_ticket: form.max_ticket ? Number(form.max_ticket) : undefined,
      });
      toast.success("Offer published");
      setForm({ ...form, name: "", promised_rate_pct: "" });
      reload();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const toggle = async (o) => {
    try { await api.patch(`/partner/offers/${o.id}`, { status: o.status === "active" ? "paused" : "active" }); reload(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-3" data-testid="partner-offers">
      <form onSubmit={create} className="glass p-4 grid grid-cols-2 md:grid-cols-3 gap-3">
        <label className="block"><span className="kpi-label">Offer name</span>
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} style={inputStyle} placeholder="Balanced 12" data-testid="offer-name" /></label>
        <label className="block"><span className="kpi-label">Promised return (% / month)</span>
          <input value={form.promised_rate_pct} onChange={(e) => setForm({ ...form, promised_rate_pct: e.target.value })} className={inputCls} style={inputStyle}
            placeholder={`${terms.promise_min_pct}–${terms.promise_max_pct}`} data-testid="offer-rate" /></label>
        <label className="block"><span className="kpi-label">Term (months)</span>
          <input value={form.term_months} onChange={(e) => setForm({ ...form, term_months: e.target.value })} className={inputCls} style={inputStyle} data-testid="offer-term" /></label>
        <label className="block"><span className="kpi-label">Client is paid</span>
          <select value={form.payout_mode} onChange={(e) => setForm({ ...form, payout_mode: e.target.value })} className={inputCls} style={inputStyle} data-testid="offer-payout">
            <option value="monthly">Every month</option>
            <option value="maturity">At the end of the term</option>
          </select></label>
        <label className="block"><span className="kpi-label">Min ticket (USDT)</span>
          <input value={form.min_ticket} onChange={(e) => setForm({ ...form, min_ticket: e.target.value })} className={inputCls} style={inputStyle}
            placeholder={String(terms.min_ticket)} data-testid="offer-min" /></label>
        <label className="block"><span className="kpi-label">Max ticket (USDT)</span>
          <input value={form.max_ticket} onChange={(e) => setForm({ ...form, max_ticket: e.target.value })} className={inputCls} style={inputStyle}
            placeholder={String(terms.max_ticket)} data-testid="offer-max" /></label>
        <div className="col-span-2 md:col-span-3 flex items-center gap-3">
          <button type="submit" className="btn-primary" data-testid="offer-create">Publish offer</button>
          <span className="text-[11.5px] text-ink-3">Your agreement allows {terms.promise_min_pct}%–{terms.promise_max_pct}% a month.</span>
        </div>
      </form>

      {!offers.length ? <div className="glass p-8 text-center text-sm text-ink-2" data-testid="partner-offers-empty">No offers yet. Publish one and it appears on your portal.</div>
        : (
          <div className="space-y-2">
            {offers.map((o) => (
              <div key={o.id} className="glass p-3.5 flex items-center gap-4 flex-wrap" data-testid={`offer-row-${o.id}`}>
                <div className="min-w-0">
                  <div className="font-semibold">{o.name}</div>
                  <div className="text-[12px] text-ink-3">{o.promised_rate_pct}% a month · {o.term_months} months · paid {o.payout_mode === "monthly" ? "monthly" : "at maturity"} · {usd(o.min_ticket)}–{usd(o.max_ticket)}</div>
                </div>
                <span className={`chip ml-auto ${o.status === "active" ? "chip-emerald" : ""}`}>{o.status}</span>
                <button onClick={() => toggle(o)} className="btn-secondary" data-testid={`offer-toggle-${o.id}`}>
                  {o.status === "active" ? <><PauseCircle size={13} /> Pause</> : <><PlayCircle size={13} /> Activate</>}
                </button>
              </div>
            ))}
          </div>
        )}
    </div>
  );
}

function Earnings() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/partner/earnings").then(({ data }) => setD(data)).catch(() => setD({ months: [], totals: {}, by_client: [] })); }, []);

  const csv = () => {
    const rows = [["Month", "Clients", "Client capital", "Owed to clients", "Your earnings", "Status"]];
    (d.months || []).forEach((m) => rows.push([m.month, m.clients, m.capital, m.client_obligation,
      m.my_earnings, m.paid ? "paid" : m.booked ? "due" : "accruing"]));
    rows.push([]);
    rows.push(["Client", "Plan", "Capital", "Owed to them", "Your earnings"]);
    (d.by_client || []).forEach((c) => rows.push([c.name, c.plan || "", c.capital, c.client_obligation, c.my_earnings]));
    const blob = new Blob([rows.map((r) => r.map((v) => `"${String(v ?? "")}"`).join(",")).join("\n")],
      { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `earnings-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  if (!d) return <div className="glass p-6 text-sm text-ink-2" data-testid="partner-earnings-loading">Adding up your book…</div>;
  const t = d.totals || {};

  return (
    <div className="space-y-4" data-testid="partner-earnings">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[["Earned to date", usd2(t.earned), TrendingUp], ["Paid to you", usd2(t.paid), CheckCircle2],
          ["Due to you", usd2(t.owed), Wallet], ["Accruing this month", usd2(t.accruing), Clock]].map(([k, v, Icon]) => (
          <div key={k} className="glass p-4" data-testid={`earnings-kpi-${k.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <div className="flex items-center gap-2 kpi-label"><Icon size={12} /> {k}</div>
            <div className="font-display text-2xl font-semibold mt-1">{v}</div>
          </div>
        ))}
      </div>

      <EarningsCharts d={d} />

      <div className="glass p-0 overflow-x-auto">
        <div className="px-4 pt-4 pb-2">
          <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-3">Month by month</span>
          <span className="text-[11.5px] text-ink-3 ml-2">
            You earn {d.split_pct}% of everything your clients' capital makes above the return you promised them.
          </span>
          {!!d.months.length && (
            <button type="button" onClick={csv} className="btn-secondary float-right -mt-1" data-testid="earnings-export-csv">
              <Download size={13} /> Export CSV
            </button>
          )}
        </div>
        {!d.months.length ? (
          <div className="px-4 pb-5 text-sm text-ink-2" data-testid="partner-earnings-empty">
            Nothing has run yet. Once a client's funds are confirmed, your share starts accruing here every month.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[11px] uppercase tracking-wider text-ink-3">
              <th className="px-4 py-2.5">Month</th><th>Clients</th><th>Client capital</th>
              <th>Owed to your clients</th><th>Your earnings</th><th>Status</th>
            </tr></thead>
            <tbody>
              {d.months.map((m) => (
                <tr key={m.month} className="border-t" style={{ borderColor: "rgba(15,18,38,0.06)" }} data-testid={`earnings-row-${m.month}`}>
                  <td className="px-4 py-2.5 font-semibold">{m.month}</td>
                  <td>{m.clients}</td>
                  <td>{usd(m.capital)}</td>
                  <td>{usd2(m.client_obligation)}</td>
                  <td className="font-semibold" style={{ color: "#0B8F78" }}>{usd2(m.my_earnings)}</td>
                  <td>
                    <span className={`chip ${m.paid ? "chip-emerald" : ""}`}>
                      {m.paid ? "paid" : m.booked ? "due" : "accruing"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {!!d.by_client?.length && (
        <div className="glass p-0 overflow-x-auto">
          <div className="px-4 pt-4 pb-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-3">By client</div>
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[11px] uppercase tracking-wider text-ink-3">
              <th className="px-4 py-2.5">Client</th><th>Capital</th><th>Owed to them</th><th>Your earnings</th>
            </tr></thead>
            <tbody>
              {d.by_client.map((c) => (
                <tr key={c.client_id} className="border-t" style={{ borderColor: "rgba(15,18,38,0.06)" }} data-testid={`earnings-client-${c.reference}`}>
                  <td className="px-4 py-2.5">
                    <div className="font-semibold">{c.name}</div>
                    <div className="text-[11.5px] text-ink-3">{c.reference}</div>
                  </td>
                  <td>{usd(c.capital)}</td>
                  <td>{usd2(c.client_obligation)}</td>
                  <td className="font-semibold" style={{ color: "#0B8F78" }}>{usd2(c.my_earnings)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const Row = ({ label, hint, children }) => (
  <label className="block">
    <span className="kpi-label">{label}</span>
    {children}
    {hint && <span className="block text-[11px] text-ink-3 mt-1">{hint}</span>}
  </label>
);

const Card = ({ title, sub, children, testid }) => (
  <div className="glass p-4 space-y-3.5" data-testid={testid}>
    <div>
      <div className="font-display text-base font-semibold tracking-tight">{title}</div>
      {sub && <div className="text-[11.5px] text-ink-3 mt-0.5">{sub}</div>}
    </div>
    {children}
  </div>
);

const Picker = ({ value, onChange, options, testid }) => (
  <div className="flex flex-wrap gap-2 mt-1" data-testid={testid}>
    {options.map(([v, label]) => (
      <button key={v} type="button" onClick={() => onChange(v)}
        className="px-3 py-1.5 rounded-xl text-[12.5px] font-medium transition-all"
        style={value === v
          ? { background: "var(--primary)", color: "white", boxShadow: "0 6px 16px rgba(124,58,237,0.28)" }
          : { background: "rgba(255,255,255,0.7)", color: "rgba(15,18,38,0.66)", border: "1px solid rgba(15,18,38,0.1)" }}
        data-testid={`${testid}-${v}`}>
        {label}
      </button>
    ))}
  </div>
);

/** What the top of the client portal will actually look like. */
function HeroPreview({ f, fallbackName }) {
  const t = themeOf(f.theme);
  const onAccent = readableOn(f.accent);
  const heroSrc = heroSrcOf(f);
  const showImage = f.hero_layout !== "plain" && !!heroSrc;
  const plain = f.hero_layout === "plain";
  const name = f.portal_name || fallbackName;
  return (
    <div className="rounded-2xl overflow-hidden" style={{ border: "1px solid rgba(15,18,38,0.1)" }} data-testid="branding-preview">
      <div className="relative" style={{ background: plain ? PLAIN_HERO(f.accent) : t.pageBg, minHeight: 190 }}>
        {showImage && f.hero_layout === "image" && (
          <>
            <img src={heroSrc} alt="" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0" style={{ background: t.scrim }} />
          </>
        )}
        <div className="relative p-5">
          <div className={f.hero_layout === "split" && showImage ? "grid grid-cols-[1fr_38%] gap-4 items-center" : ""}>
            <div className="min-w-0">
              <div className="flex items-center gap-2.5">
                {f.logo
                  ? <img src={f.logo} alt="" style={{ height: 24, maxWidth: 90, objectFit: "contain" }} />
                  : <span className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: plain ? "rgba(255,255,255,0.22)" : alpha(f.accent, 0.14), color: plain ? onAccent : f.accent }}><Building2 size={14} /></span>}
                <span className="font-display text-[13px] font-semibold" style={{ color: plain ? onAccent : t.ink }}>{name}</span>
              </div>
              <div className="font-display text-[16px] font-extrabold tracking-tight mt-3" style={{ color: plain ? onAccent : t.ink, maxWidth: 300 }}>
                {f.headline || `Managed digital-asset income, arranged by ${name}.`}
              </div>
              {f.tagline && <div className="text-[11px] mt-1.5 line-clamp-3" style={{ color: plain ? alpha(onAccent, 0.85) : t.ink2, maxWidth: 300 }}>{f.tagline}</div>}
              <span className="inline-block mt-3 rounded-full px-3 py-1.5 text-[11px] font-bold"
                style={{ background: plain ? onAccent : f.accent, color: plain ? f.accent : onAccent }}>
                {f.cta_label || "Request your place"}
              </span>
            </div>
            {f.hero_layout === "split" && showImage && (
              <img src={heroSrc} alt="" className="w-full h-[130px] object-cover rounded-xl" style={{ border: `1px solid ${t.border}` }} />
            )}
          </div>
        </div>
      </div>
      <div className="px-4 py-2.5 text-[11px] text-ink-3" style={{ background: "rgba(255,255,255,0.7)" }}>
        Live preview · the real portal also carries your plans, highlights, firm details and footer.
      </div>
    </div>
  );
}

function Branding({ me, reload, portalUrl }) {
  const b = me.partner.branding || {};
  const [monthly, setMonthly] = useState(me.monthly_statements !== false);
  const [f, setF] = useState({
    portal_name: b.portal_name || me.partner.name,
    tagline: b.tagline || "",
    headline: b.headline || "",
    cta_label: b.cta_label || "",
    accent: b.accent || "#4060FF",
    theme: b.theme || "light",
    logo: b.logo || "",
    hero_layout: b.hero_layout || "image",
    hero_image: b.hero_image || "",
    hero_preset: b.hero_preset || "private-office",
    highlights: [...(b.highlights || []), "", "", "", ""].slice(0, 4),
    about_title: b.about_title || "",
    about_text: b.about_text || "",
    adviser_name: b.adviser_name || "",
    adviser_title: b.adviser_title || "",
    adviser_photo: b.adviser_photo || "",
    contact_email: b.contact_email || "",
    contact_phone: b.contact_phone || "",
    website: b.website || "",
    address: b.address || "",
    footer_note: b.footer_note || "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const pick = (k) => (v) => setF((p) => ({ ...p, [k]: v }));

  const upload = (key, resizer) => async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    try {
      const out = await resizer(file);
      setF((p) => ({ ...p, [key]: out }));
    } catch { toast.error("We couldn't read that image"); }
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.patch("/partner/me/branding", { ...f, highlights: f.highlights.map((h) => h.trim()).filter(Boolean) });
      toast.success("Your portal has been updated"); reload();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="grid lg:grid-cols-[1fr_380px] gap-4 items-start" data-testid="partner-branding">
      <div className="space-y-4">
        <Card title="Identity" sub="The name, words and colour your clients see." testid="branding-identity">
          <Row label="Portal name">
            <input value={f.portal_name} onChange={set("portal_name")} className={inputCls} style={inputStyle} data-testid="branding-name" />
          </Row>
          <Row label="Headline" hint="The big line at the top. Leave blank and we'll write one from your name.">
            <input value={f.headline} onChange={set("headline")} className={inputCls} style={inputStyle}
              placeholder="Managed digital-asset income, arranged for you." data-testid="branding-headline" />
          </Row>
          <Row label="One-line tagline">
            <input value={f.tagline} onChange={set("tagline")} className={inputCls} style={inputStyle}
              placeholder="Steady monthly income, reviewed with you before a penny moves" data-testid="branding-tagline" />
          </Row>
          <Row label="Button wording">
            <input value={f.cta_label} onChange={set("cta_label")} className={inputCls} style={inputStyle}
              placeholder="Request your place" data-testid="branding-cta" />
          </Row>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <span className="kpi-label">Accent colour</span>
              <div className="flex items-center gap-2 mt-1">
                <input type="color" value={f.accent} onChange={set("accent")} className="w-10 h-10 rounded-lg" data-testid="branding-accent" />
                <input value={f.accent} onChange={set("accent")} className="px-3 py-2 rounded-xl text-sm font-mono w-32" style={inputStyle} />
              </div>
            </div>
            <div>
              <span className="kpi-label">Theme</span>
              <Picker value={f.theme} onChange={pick("theme")} options={[["light", "Light"], ["dark", "Dark"]]} testid="branding-theme" />
            </div>
          </div>
          <div>
            <span className="kpi-label">Logo</span>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <label className="btn-secondary cursor-pointer" data-testid="branding-logo-label">
                <Upload size={13} /> Upload
                <input type="file" accept="image/*" className="hidden" data-testid="branding-logo-upload" onChange={upload("logo", resizeLogo)} />
              </label>
              <input value={f.logo.startsWith("data:") ? "" : f.logo} onChange={set("logo")}
                placeholder="or paste an https link" className="flex-1 min-w-[180px] px-3 py-2 rounded-xl text-sm" style={inputStyle} data-testid="branding-logo-url" />
              {f.logo && <button onClick={() => setF({ ...f, logo: "" })} className="btn-secondary" data-testid="branding-logo-clear">Remove</button>}
            </div>
          </div>
        </Card>

        <Card title="Hero" sub="The first thing a client sees. Use your own photo, or one of ours." testid="branding-hero">
          <div>
            <span className="kpi-label">Layout</span>
            <Picker value={f.hero_layout} onChange={pick("hero_layout")}
              options={[["image", "Full image"], ["split", "Text + image"], ["plain", "Plain colour"]]} testid="branding-layout" />
          </div>
          {f.hero_layout !== "plain" && (
            <>
              <div>
                <span className="kpi-label">Your own image</span>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <label className="btn-secondary cursor-pointer" data-testid="branding-hero-label">
                    <ImagePlus size={13} /> Upload photo
                    <input type="file" accept="image/*" className="hidden" data-testid="branding-hero-upload" onChange={upload("hero_image", resizePhoto)} />
                  </label>
                  <input value={f.hero_image.startsWith("data:") ? "" : f.hero_image} onChange={set("hero_image")}
                    placeholder="or paste an https link" className="flex-1 min-w-[180px] px-3 py-2 rounded-xl text-sm" style={inputStyle} data-testid="branding-hero-url" />
                  {f.hero_image && <button onClick={() => setF({ ...f, hero_image: "" })} className="btn-secondary" data-testid="branding-hero-clear">Remove</button>}
                </div>
                <span className="block text-[11px] text-ink-3 mt-1">Wide shots work best — we resize it for you. Remove it to fall back to a background below.</span>
              </div>
              <div>
                <span className="kpi-label">Or pick a background</span>
                <div className="grid grid-cols-3 gap-2 mt-1.5" data-testid="branding-presets">
                  {HERO_PRESETS.map(([id, label]) => {
                    const on = !f.hero_image && f.hero_preset === id;
                    return (
                      <button key={id} type="button" onClick={() => setF({ ...f, hero_preset: id, hero_image: "" })}
                        className="rounded-xl overflow-hidden text-left transition-transform hover:-translate-y-0.5"
                        style={{ border: on ? `2px solid ${f.accent}` : "2px solid transparent", outline: on ? "none" : "1px solid rgba(15,18,38,0.1)" }}
                        data-testid={`branding-preset-${id}`}>
                        <img src={`/portal/${id}.jpg`} alt={label} className="w-full h-14 object-cover" />
                        <div className="px-2 py-1 text-[10.5px] font-medium bg-white/80">{label}</div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </Card>

        <Card title="Highlights" sub="Up to four short lines of reassurance, in a strip under the hero." testid="branding-highlights">
          {f.highlights.map((h, i) => (
            <input key={i} value={h}
              onChange={(e) => setF((p) => { const next = [...p.highlights]; next[i] = e.target.value; return { ...p, highlights: next }; })}
              className={inputCls} style={inputStyle} maxLength={48}
              placeholder={["Independent adviser since 2009", "Monthly income, paid on time", "Every application reviewed personally", "Statements you can check any day"][i]}
              data-testid={`branding-highlight-${i}`} />
          ))}
        </Card>

        <Card title="About your firm" sub="Optional — but this is what makes the page feel like a real firm." testid="branding-about">
          <Row label="Section title">
            <input value={f.about_title} onChange={set("about_title")} className={inputCls} style={inputStyle}
              placeholder="Who we are" data-testid="branding-about-title" />
          </Row>
          <Row label="What you want them to know">
            <textarea value={f.about_text} onChange={set("about_text")} rows={5} className={inputCls} style={inputStyle}
              placeholder="A few honest sentences about your firm, who you look after, and how you work."
              data-testid="branding-about-text" />
          </Row>
          <div className="grid sm:grid-cols-2 gap-3">
            <Row label="Adviser name">
              <input value={f.adviser_name} onChange={set("adviser_name")} className={inputCls} style={inputStyle} data-testid="branding-adviser-name" />
            </Row>
            <Row label="Their title">
              <input value={f.adviser_title} onChange={set("adviser_title")} className={inputCls} style={inputStyle}
                placeholder="Managing partner" data-testid="branding-adviser-title" />
            </Row>
          </div>
          <div>
            <span className="kpi-label">Adviser photo</span>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              {f.adviser_photo && <img src={f.adviser_photo} alt="" className="w-10 h-10 rounded-full object-cover" />}
              <label className="btn-secondary cursor-pointer" data-testid="branding-adviser-label">
                <ImagePlus size={13} /> Upload
                <input type="file" accept="image/*" className="hidden" data-testid="branding-adviser-upload" onChange={upload("adviser_photo", (file) => resizePhoto(file, 800))} />
              </label>
              {f.adviser_photo && <button onClick={() => setF({ ...f, adviser_photo: "" })} className="btn-secondary" data-testid="branding-adviser-clear">Remove</button>}
            </div>
          </div>
        </Card>

        <Card title="Contact & footer" sub="Shown at the bottom of your portal." testid="branding-contact">
          <div className="grid sm:grid-cols-2 gap-3">
            <Row label="Email"><input value={f.contact_email} onChange={set("contact_email")} className={inputCls} style={inputStyle} data-testid="branding-contact-email" /></Row>
            <Row label="Phone"><input value={f.contact_phone} onChange={set("contact_phone")} className={inputCls} style={inputStyle} data-testid="branding-contact-phone" /></Row>
          </div>
          <Row label="Website"><input value={f.website} onChange={set("website")} className={inputCls} style={inputStyle} placeholder="yourfirm.com" data-testid="branding-website" /></Row>
          <Row label="Address"><textarea value={f.address} onChange={set("address")} rows={2} className={inputCls} style={inputStyle} data-testid="branding-address" /></Row>
          <Row label="Small print" hint="Company number, registration line, anything your compliance needs on the page.">
            <textarea value={f.footer_note} onChange={set("footer_note")} rows={2} className={inputCls} style={inputStyle} data-testid="branding-footer-note" />
          </Row>
        </Card>

        <Card title="Monthly statements" sub="The only email we send your clients on a schedule." testid="branding-monthly">
          <label className="flex items-start gap-3 cursor-pointer">
            <input type="checkbox" checked={monthly} className="mt-1"
              onChange={async (e) => {
                const v = e.target.checked;
                setMonthly(v);
                try {
                  await api.post("/partner/settings", { monthly_statements: v });
                  toast.success(v ? "We'll send your clients their statement on the 1st" : "Monthly statements switched off");
                } catch (err) { setMonthly(!v); toast.error(formatApiError(err.response?.data?.detail)); }
              }}
              data-testid="branding-monthly-toggle" />
            <span className="text-[13px] text-ink-2">
              On the 1st of each month, email every client their one-page statement PDF — sent in
              <b> your</b> name and colour, from your contact details. Switch this off and we'll never
              email your clients on a schedule.
            </span>
          </label>
        </Card>
      </div>

      <div className="lg:sticky lg:top-5 space-y-3">
        <HeroPreview f={f} fallbackName={me.partner.name} />
        <div className="glass p-4 space-y-2.5">
          <button onClick={save} disabled={busy} className="btn-primary w-full justify-center" data-testid="branding-save">
            {busy ? "Saving…" : "Save my portal"}
          </button>
          <a href={portalUrl} target="_blank" rel="noreferrer" className="btn-secondary w-full justify-center" data-testid="branding-open-portal">
            <ExternalLink size={13} /> Open my portal
          </a>
          <p className="text-[11px] text-ink-3">
            Anything you leave blank is simply left off the page — nothing shows up empty.
          </p>
        </div>
      </div>
    </div>
  );
}

export default function PartnerConsole() {
  const { user, logout } = useAuth();
  const [me, setMe] = useState(null);
  const [offerData, setOfferData] = useState(null);
  const [tab, setTab] = useState("Overview");
  const [error, setError] = useState("");

  const load = async () => {
    try {
      const [{ data: m }, { data: o }] = await Promise.all([api.get("/partner/me"), api.get("/partner/offers")]);
      setMe(m); setOfferData(o); setError("");
    } catch (err) { setError(formatApiError(err.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" data-testid="partner-console-error">
        <div className="glass-strong p-8 max-w-md text-center">
          <Building2 size={26} className="mx-auto mb-3 text-ink-3" />
          <div className="font-display text-xl font-semibold">Partner console unavailable</div>
          <p className="text-sm text-ink-2 mt-2">{error}</p>
          <button onClick={logout} className="btn-secondary mt-4"><LogOut size={14} /> Sign out</button>
        </div>
      </div>
    );
  }
  if (!me) return <div className="min-h-screen flex items-center justify-center text-sm text-ink-2" data-testid="partner-console-loading">Loading your console…</div>;

  const accent = me.partner.branding?.accent || "#4060FF";
  const portalUrl = `${window.location.origin}${me.portal_path}`;

  return (
    <div className="min-h-screen pb-16" data-testid="partner-console-page">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 pt-5">
        <div className="glass px-5 py-3.5 flex items-center gap-4 flex-wrap mb-5">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `${accent}1f` }}>
            {me.partner.branding?.logo
              ? <img src={me.partner.branding.logo} alt="" style={{ width: 24, height: 24, objectFit: "contain" }} />
              : <Building2 size={16} style={{ color: accent }} />}
          </div>
          <div className="min-w-0">
            <div className="font-display text-lg font-semibold leading-tight" data-testid="partner-console-name">{me.partner.name}</div>
            <div className="text-[11.5px] text-ink-3">Partner console · {me.me.email}</div>
          </div>
          <nav className="flex items-center gap-1 ml-auto flex-wrap">
            {TABS.map((t) => (
              <button key={t} onClick={() => setTab(t)} data-testid={`partner-tab-${t.toLowerCase()}`}
                className="px-3 py-1.5 rounded-lg text-sm font-medium transition-colors"
                style={tab === t ? { background: accent, color: "white" } : { color: "rgba(15,18,38,0.62)" }}>
                {t}
              </button>
            ))}
            {user?.partner_access && user?.role !== "partner" && (
              <a href="/dashboard" className="btn-secondary ml-2" data-testid="partner-to-member-dashboard"
                title="Your own BotChainCP member dashboard — same account">
                <LayoutDashboard size={13} /> My dashboard
              </a>
            )}
            <button onClick={logout} className="btn-secondary ml-2" data-testid="partner-logout"><LogOut size={13} /> Sign out</button>
          </nav>
        </div>

        {tab === "Overview" && <Overview me={me} portalUrl={portalUrl} />}
        {tab === "Clients" && <Clients offers={offerData?.offers || []} reload={load} />}
        {tab === "Earnings" && <Earnings />}
        {tab === "Offers" && <Offers offers={offerData?.offers || []} terms={offerData?.terms || {}} reload={load} />}
        {tab === "Branding" && <Branding me={me} reload={load} portalUrl={portalUrl} />}
      </div>
    </div>
  );
}
